<script setup>
import { useAxiosClient } from '@/composable/useFetch'
import { computed, ref, watch } from 'vue'
import { i18n } from '@/i18n'
import ReportChartTypeSelector from './ReportChartTypeSelector.vue'
import { Fragment } from 'vue-fragment'

const props = defineProps({
  filter: {
    type: Object,
    default: () => ({}),
  },
  valueFormatting: {
    type: Function,
    default: value => value,
  },
})

const types = [
  { label: i18n.t('dashboard.area_selector.province'), value: 'province' },
  { label: i18n.t('dashboard.area_selector.city'), value: 'city' },
]

const type = ref('province')
const typeLabel = computed(() => types.find(item => item.value === type.value)?.label)

watch(props.filter, () => (type.value = 'province'), { deep: true })

const state = useAxiosClient(
  () => `/bi/dashboard2/dash_ord_date/chart/order_by_${type.value}`,
  () => ({
    params: {
      ...props.filter,
    },
  }),
  {
    watch: [props.filter, type],
    immediate: true,
    initialData: [],
    transform(data) {
      return data.map((item, index) => {
        const value = item['countAggr'] || 0

        return {
          ...item,
          title: type.value === 'province' ? item.provinceName : item.cityName,
          value,
          tooltip: i18n.tc(
            `dashboard.${type.value.toLowerCase()}.tooltip`,
            value,
            { value: props.valueFormatting(value) },
          ),
          formattedValue: props.valueFormatting(value),
        }
      })
    },
  },
)

const total = computed(() =>
  state.state.value.reduce((total, item) => total + item.value, 0),
)

function getPercentage(value) {
  return ((value / total.value) * 100).toFixed(2)
}
</script>

<template>
  <div>
    <div class="mb-3">
      <h2>
        {{ $t('dashboard.top_area_title') }}

        <ReportChartTypeSelector
          v-model="type"
          :options="types"
        />
      </h2>
      <div class="subtitle">
        {{ $t('dashboard.top_area_subtitle') }}
      </div>
    </div>

    <div class="list-chart-container">
      <div class="list-item list-item--header">
        <div>{{ typeLabel }}</div>
        <div>{{ $t('dashboard.total_order_text') }}</div>
      </div>

      <template v-if="state.isLoading.value">
        <div v-for="i in 10" :key="i" class="list-item">
          <div>
            <a-skeleton active :paragraph="{ rows: 1 }" :title="false" />
          </div>
          <div>
            <a-skeleton
              active
              :paragraph="{ rows: 1 }"
              :title="false"
              class="text-right"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <template v-if="state.state.value.length">
          <div
            v-for="item in state.state.value"
            :key="item.title"
            class="list-item"
            :style="{ '--value': getPercentage(item.value) }"
          >
            <div>{{ item.title }}</div>
            <div>{{ $props.valueFormatting(item.value) }}</div>
          </div>
        </template>
        <div v-else class="list-item empty">
          <div>{{ $t('dashboard.top_product_empty') }}</div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.list-item {
  font-size: 0.875rem;
  display: grid;
  grid-template-columns: 1fr 125px;
  border-bottom: 1px solid var(--border-light);
  position: relative;

  > div {
    color: var(--text-color-secondary);
    padding: 0.5rem 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > div:nth-child(even) {
    text-align: right;
    // color: var(--text-muted)
  }

  &--header > div {
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-muted);
    font-size: 0.75rem;
  }

  &::after {
    content: '';
    width: calc(var(--value, 0) * 1%);
    height: 3px;
    background-color: var(--kit-color-primary);
    position: absolute;
    bottom: -2px;
    border-radius: 2px 100px 100px 2px;
  }
}

.ant-skeleton {
  ::v-deep {
    .ant-skeleton-paragraph {
      margin: 0;
    }
  }

  &.text-right {
    ::v-deep {
      .ant-skeleton-paragraph li {
        margin-left: auto;
      }
    }
  }
}
</style>
